import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { DeleteButton } from '@/components/DeleteConfirmDialog';
import { Package, Edit, AlertTriangle } from 'lucide-react';

const LagerPage = () => {
  const { API, user } = useContext(AuthContext);
  const [bestaende, setBestaende] = useState([]);
  const [produkte, setProdukte] = useState({});
  const [automaten, setAutomaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editQuantity, setEditQuantity] = useState(0);
  const [editNote, setEditNote] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bestaendeRes, produkteRes, automatenRes] = await Promise.all([
        axios.get(`${API}/lagerbestaende`),
        axios.get(`${API}/produkte`),
        axios.get(`${API}/automaten`),
      ]);
      setBestaende(bestaendeRes.data || []);
      setAutomaten(automatenRes.data || []);
      
      const produkteMap = {};
      (produkteRes.data || []).forEach(p => {
        produkteMap[p.id] = p;
      });
      setProdukte(produkteMap);
    } catch (error) {
      // Nur bei echten Serverfehlern einen Toast zeigen
      if (error.response?.status >= 500) {
        toast.error('Serverfehler beim Laden der Lagerdaten');
      } else if (error.response?.status === 403) {
        toast.error('Keine Berechtigung für Lager');
      }
      setBestaende([]);
      setAutomaten([]);
      setProdukte({});
      console.error('Lager laden:', error.response?.status || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Dashboard-Aggregation: Betriebsbereite Automaten (Lager VOLL)
  // Stückzahl = Anzahl der Automaten, NICHT die Füllmenge
  const getBetriebsbereitData = () => {
    const vollAutomaten = automaten.filter(a => a.status === 'Lager VOLL');
    const grouped = {};
    
    vollAutomaten.forEach(a => {
      const currentFill = a.current_fill || {};
      // Hole den ersten Slot mit Produktname
      const fillData = currentFill['1'] || Object.values(currentFill)[0];
      const productName = fillData?.product_name || 'Unbekannt';
      const coinType = a.coin_type || '?';
      
      const key = `${productName}|${coinType}`;
      if (!grouped[key]) {
        grouped[key] = {
          productName,
          coinType,
          count: 0  // Anzahl der Automaten, NICHT Füllmenge
        };
      }
      grouped[key].count += 1;  // Zähle Automaten, nicht Füllmenge
    });
    
    return Object.values(grouped).sort((a, b) => a.productName.localeCompare(b.productName));
  };

  // Dashboard-Aggregation: Befüllbare Automaten (Lager LEER)
  // IMMER die vier fixen Münztypen anzeigen, auch mit 0
  const getBefuellbarData = () => {
    // Fixe Münztypen in dieser Reihenfolge
    const fixedCoinTypes = ['0,20', '0,50', '1,00', '2,00'];
    const leerAutomaten = automaten.filter(a => a.status === 'Lager LEER');
    
    // Initialisiere alle fixen Münztypen mit 0
    const grouped = {};
    fixedCoinTypes.forEach(ct => {
      grouped[ct] = { coinType: ct, count: 0 };
    });
    
    // Zähle die tatsächlichen Automaten
    leerAutomaten.forEach(a => {
      // Normalisiere den Münztyp (entferne € falls vorhanden, trimme)
      let coinType = (a.coin_type || '').replace('€', '').trim();
      
      // Mappe auf die fixen Werte
      if (grouped[coinType]) {
        grouped[coinType].count += 1;
      }
    });
    
    // Gib in fixer Reihenfolge zurück
    return fixedCoinTypes.map(ct => grouped[ct]);
  };

  const handleEdit = (bestand) => {
    setEditingItem(bestand);
    setEditQuantity(bestand.quantity);
    setEditNote('');
    setEditDialogOpen(true);
  };

  const handleDelete = async (bestand) => {
    try {
      console.log('Setting stock to 0 for:', bestand.product_id);
      await axios.put(`${API}/lagerbestaende/${bestand.product_id}`, {
        product_id: bestand.product_id,
        quantity: 0,
        note: 'Aus Lager entfernt'
      });
      console.log('Stock set to 0 successfully');
      toast.success('Artikel aus Lager entfernt!');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Fehler beim Entfernen');
      throw error;
    }
  };

  const handleSaveEdit = async () => {
    // Validierung: Menge muss >= 0 sein
    const quantity = parseInt(editQuantity);
    if (isNaN(quantity)) {
      toast.error('Bitte geben Sie eine gültige Zahl ein');
      return;
    }
    if (quantity < 0) {
      toast.error('Die Menge darf nicht negativ sein');
      return;
    }
    
    try {
      console.log('Updating lagerbestand:', editingItem.product_id, 'New quantity:', quantity);
      const response = await axios.put(`${API}/lagerbestaende/${editingItem.product_id}`, {
        quantity: quantity,
        note: editNote.trim() || `Bestand korrigiert am ${new Date().toLocaleDateString('de-DE')}`,
      });
      toast.success(`Bestand auf ${quantity} aktualisiert!`);
      setEditDialogOpen(false);
      setEditingItem(null);
      setEditQuantity(0);
      setEditNote('');
      fetchData();
    } catch (error) {
      console.error('Update error:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Aktualisieren';
      toast.error(errorMsg);
    }
  };

  const getLowStockItems = () => {
    return bestaende.filter(b => {
      const produkt = produkte[b.product_id];
      return produkt && produkt.active !== false && b.quantity < produkt.min_stock;
    });
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  const lowStockItems = getLowStockItems();
  const betriebsbereitData = getBetriebsbereitData();
  const befuellbarData = getBefuellbarData();

  return (
    <div className="space-y-6 animate-slide-in" data-testid="lager-page">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
          Lager
        </h1>
        <p className="text-slate-600">Lagerbestände verwalten</p>
      </div>

      {/* Admin Dashboard: Automatenübersicht */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="lager-dashboard">
          {/* Betriebsbereit (Lager VOLL) */}
          <Card className="p-6">
            <h2 className="text-lg font-bold mb-4 text-slate-700">Betriebsbereit</h2>
            {betriebsbereitData.length === 0 ? (
              <p className="text-slate-500 text-sm">Keine Automaten im Status Lager VOLL</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-slate-500">Füllung</th>
                    <th className="pb-2 font-medium text-slate-500">Einwurf</th>
                    <th className="pb-2 font-medium text-slate-500 text-right">Stückzahl</th>
                  </tr>
                </thead>
                <tbody>
                  {betriebsbereitData.map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 text-slate-700">{item.productName}</td>
                      <td className="py-2 text-slate-600">{item.coinType} €</td>
                      <td className="py-2 text-slate-700 text-right font-medium">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Befüllbar (Lager LEER) - IMMER alle 4 Münztypen anzeigen */}
          <Card className="p-6">
            <h2 className="text-lg font-bold mb-4 text-slate-700">Befüllbar</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-slate-500">Einwurf</th>
                  <th className="pb-2 font-medium text-slate-500 text-right">Stückzahl</th>
                </tr>
              </thead>
              <tbody>
                {befuellbarData.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 text-slate-600">{item.coinType} €</td>
                    <td className="py-2 text-slate-700 text-right font-medium">{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {lowStockItems.length > 0 && (
        <Card className="p-6 bg-amber-50 border-amber-200">
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangle className="text-amber-600" size={28} />
            <div>
              <h2 className="text-xl font-bold text-amber-900">Einkaufsliste</h2>
              <p className="text-sm text-amber-700">Produkte unter Mindestbestand</p>
            </div>
          </div>
          <div className="space-y-2">
            {lowStockItems.map((bestand) => {
              const produkt = produkte[bestand.product_id];
              if (!produkt) return null;
              // Empfohlene Bestellmenge = Mindestbestand - realistischer Bestand
              const empfohlen = Math.max(0, produkt.min_stock - bestand.quantity);
              return (
                <div
                  key={bestand.id}
                  className="p-3 bg-white rounded-lg flex justify-between items-center"
                  data-testid={`low-stock-${produkt.name}`}
                >
                  <div>
                    <p className="font-medium">{produkt.name}</p>
                    <p className="text-sm text-slate-600">
                      Aktuell: {bestand.quantity} | Mindestbestand: {produkt.min_stock}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Empfohlene Bestellung:</p>
                    <p className="font-bold text-lg">{empfohlen} Stück</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="p-6">
        <h2 className="text-xl font-bold mb-4">Alle Lagerbestände</h2>
        <div className="space-y-3">
          {bestaende.map((bestand) => {
            const produkt = produkte[bestand.product_id];
            if (!produkt) return null;
            const isLow = bestand.quantity < produkt.min_stock;
            return (
              <div
                key={bestand.id}
                className={`p-4 rounded-lg border-2 ${
                  isLow ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                }`}
                data-testid={`stock-item-${produkt.name}`}
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-xl ${
                      isLow ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      <Package className={isLow ? 'text-red-600' : 'text-blue-600'} size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{produkt.name}</h3>
                      <p className="text-sm text-slate-600">
                        Mindestbestand: {produkt.min_stock} | Füllmenge: {produkt.default_fill_quantity} | Kategorie: {produkt.category || 'Keine'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Aktueller Bestand</p>
                      <p className={`text-2xl font-bold ${
                        isLow ? 'text-red-600' : 'text-slate-900'
                      }`}>
                        {bestand.quantity}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(bestand)}
                      data-testid={`edit-button-${produkt.name}`}
                    >
                      <Edit size={16} />
                    </Button>
                    <DeleteButton
                      onDelete={() => handleDelete(bestand)}
                      title="Aus Lager entfernen"
                      itemName={produkt.name}
                      description={`Möchten Sie "${produkt.name}" aus dem Lager entfernen? (Bestand wird auf 0 gesetzt)`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Bestand korrigieren - {editingItem && produkte[editingItem.product_id]?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Aktuelle Menge</Label>
              <p className="text-lg font-bold text-slate-700 mb-2">
                {editingItem?.quantity ?? 0} Stück
                {editingItem?.quantity < 0 && (
                  <span className="text-red-500 text-sm ml-2">(Minusbestand)</span>
                )}
              </p>
            </div>
            <div>
              <Label>Neue Menge</Label>
              <Input
                type="number"
                min="0"
                value={editQuantity}
                onChange={(e) => {
                  const val = e.target.value;
                  // Erlaube leeres Feld während der Eingabe
                  if (val === '' || val === '-') {
                    setEditQuantity(val);
                  } else {
                    const num = parseInt(val);
                    if (!isNaN(num)) {
                      setEditQuantity(num);
                    }
                  }
                }}
                data-testid="edit-quantity-input"
                className={editQuantity < 0 ? 'border-red-500' : ''}
              />
              {editQuantity < 0 && (
                <p className="text-red-500 text-xs mt-1">Menge darf nicht negativ sein</p>
              )}
            </div>
            <div>
              <Label>Notiz (Optional)</Label>
              <Textarea
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Optionale Notiz zur Korrektur..."
                rows={3}
                data-testid="edit-note-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Wird automatisch mit Datum versehen, falls leer
              </p>
            </div>
            <Button onClick={handleSaveEdit} className="w-full" data-testid="save-edit-button">
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LagerPage;